/**
 * A small ring buffer of what happened in the app (errors, lifecycle, service worker events),
 * kept in memory and mirrored to localStorage so the next start can still show it after the page
 * was killed. Pure: the clock, storage and timer are injected, so it runs under node:test.
 */
import { clipLine } from "./redact.ts";

export interface LogEntry {
  /** Epoch ms. */
  t: number;
  /** Session id: one per page load. */
  sid: string;
  kind: string;
  msg: string;
}

export const LOG_CAPACITY = 100;
export const LOG_KEY = "walk-player.diaglog";
export const MESSAGE_MAX = 300;
const PERSIST_DELAY_MS = 1500;

type LogStorage = Pick<Storage, "getItem" | "setItem">;

export interface EventLogOptions {
  sessionId: string;
  now?: () => number;
  storage?: LogStorage | null;
  capacity?: number;
  /** Runs `fn` later; the default is setTimeout. Tests pass a fake. */
  schedule?: (fn: () => void, ms: number) => void;
  persistDelayMs?: number;
}

/** What a stored log may hold: anything else is dropped, never thrown. */
export function parseStoredLog(raw: string | null | undefined, capacity = LOG_CAPACITY): LogEntry[] {
  if (!raw) return [];
  try {
    const data = JSON.parse(raw) as unknown;
    if (!Array.isArray(data)) return [];
    const entries: LogEntry[] = [];
    for (const item of data) {
      const e = item as Partial<LogEntry> | null;
      if (e && typeof e.t === "number" && typeof e.sid === "string" && typeof e.kind === "string" && typeof e.msg === "string") {
        entries.push({ t: e.t, sid: e.sid, kind: e.kind, msg: e.msg });
      }
    }
    return entries.slice(-capacity);
  } catch {
    return [];
  }
}

export class EventLog {
  readonly sessionId: string;
  private readonly now: () => number;
  private readonly storage: LogStorage | null;
  private readonly capacity: number;
  private readonly schedule: (fn: () => void, ms: number) => void;
  private readonly delay: number;
  private buffer: LogEntry[];
  private pending = false;

  constructor(options: EventLogOptions) {
    this.sessionId = options.sessionId;
    this.now = options.now ?? Date.now;
    this.storage = options.storage ?? null;
    this.capacity = options.capacity ?? LOG_CAPACITY;
    this.schedule = options.schedule ?? ((fn, ms) => void setTimeout(fn, ms));
    this.delay = options.persistDelayMs ?? PERSIST_DELAY_MS;
    let stored: string | null = null;
    try {
      stored = this.storage?.getItem(LOG_KEY) ?? null;
    } catch {
      // Storage blocked: the log lives in memory only.
    }
    this.buffer = parseStoredLog(stored, this.capacity);
  }

  add(kind: string, message: string): void {
    this.buffer.push({ t: this.now(), sid: this.sessionId, kind, msg: clipLine(message, MESSAGE_MAX) });
    if (this.buffer.length > this.capacity) this.buffer.splice(0, this.buffer.length - this.capacity);
    if (this.storage && !this.pending) {
      this.pending = true;
      this.schedule(() => this.flush(), this.delay);
    }
  }

  /** Writes now (also called when the page is hidden: a hidden page may be killed without another chance). */
  flush(): void {
    this.pending = false;
    if (!this.storage) return;
    try {
      this.storage.setItem(LOG_KEY, JSON.stringify(this.buffer));
    } catch {
      // Quota or blocked: keep the in-memory log.
    }
  }

  /** Oldest first: earlier sessions (from storage) then this one. */
  entries(): LogEntry[] {
    return this.buffer.slice();
  }
}
